import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  PlugZap,
  Wifi,
  Users2,
  Boxes,
  Landmark,
  ArrowRight,
  Activity,
  Recycle,
} from 'lucide-react';
import { fetchStats } from '../api/stats';
import type { StatsByEnv, EnvStats, Env, LifecycleStatus } from '../types';
import { LifecycleBadge, LIFECYCLE_ORDER, LIFECYCLE_META } from '../components/LifecycleBadge';

const ENVS: Env[] = ['prod', 'staging'];

function fmtEur(v: number): string {
  return `${v.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;
}

// Couleur d'accent par métrique (icône dans une pastille teintée).
const STAT_ACCENTS = {
  devices: 'stat-ico-cyan',
  online: 'stat-ico-emerald',
  clients: 'stat-ico-violet',
  types: 'stat-ico-amber',
  value: 'stat-ico-teal',
} as const;

function Stat({
  icon,
  accent,
  label,
  value,
  hint,
}: {
  readonly icon: ReactNode;
  readonly accent: string;
  readonly label: string;
  readonly value: string | number;
  readonly hint?: string;
}) {
  return (
    <div className="stat-cell">
      <div className="flex items-center gap-2.5">
        <span className={`stat-ico ${accent}`}>{icon}</span>
        <span className="kpi-label">{label}</span>
      </div>
      <div className="kpi-value">{value}</div>
      {hint && <div className="text-xs t-faint">{hint}</div>}
    </div>
  );
}

// Statuts wattelzce -> pill colorée (visuel uniquement).
function statusPill(status: string): string {
  if (status === 'active') return 'pill pill-green';
  if (status === 'maintenance') return 'pill pill-amber';
  if (status === 'unavailable') return 'pill pill-orange';
  return 'pill pill-gray';
}

// Couleur de segment du pipeline par étape (assortie aux pills du cycle de vie).
const STAGE_BAR: Record<LifecycleStatus, string> = {
  in_stock: 'bg-slate-500',
  testing: 'bg-amber-400',
  in_transit: 'bg-sky-400',
  installed: 'bg-indigo-400',
  in_service: 'bg-emerald-400',
  in_maintenance: 'bg-red-400',
  suspended: 'bg-orange-400',
  decommissioned: 'bg-slate-600',
};

function EnvSection({ stats }: { readonly stats: EnvStats }) {
  const isProd = stats.env === 'prod';
  const statuses = Object.entries(stats.byStatus).sort((a, b) => b[1] - a[1]);
  const lifecycleEntries = stats.byLifecycle
    ? LIFECYCLE_ORDER.filter((s) => (stats.byLifecycle![s] ?? 0) > 0)
    : [];
  const lifecycleTotal = lifecycleEntries.reduce((sum, s) => sum + (stats.byLifecycle![s] ?? 0), 0);

  return (
    <section className="panel overflow-hidden">
      {/* En-tête d'environnement, halo teinté prod/staging */}
      <div
        className="panel-header"
        style={{
          backgroundImage: isProd
            ? 'linear-gradient(to right, rgba(244,63,94,0.08), transparent 60%)'
            : 'linear-gradient(to right, rgba(34,211,238,0.08), transparent 60%)',
        }}
      >
        <div className="flex items-center gap-3">
          <span className={isProd ? 'pill-env-prod' : 'pill-env-staging'}>
            <span className="pill-dot" />
            {stats.env}
          </span>
          <h2 className="panel-title">{isProd ? 'Production' : 'Staging'}</h2>
        </div>
        <Link
          to="/inventaire"
          className="t-link text-[13px] inline-flex items-center gap-1 hover:underline"
        >
          Ouvrir l'inventaire <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="p-5 space-y-5">
        {/* KPI iconifiés */}
        <div className="stat-strip sm:grid-cols-3 lg:grid-cols-5">
          <Stat
            icon={<PlugZap className="h-4 w-4" />}
            accent={STAT_ACCENTS.devices}
            label="Equipements"
            value={stats.devices}
          />
          <Stat
            icon={<Wifi className="h-4 w-4" />}
            accent={STAT_ACCENTS.online}
            label="En ligne"
            value={stats.online}
            hint="connectees (MQTT ou OCPP)"
          />
          <Stat
            icon={<Users2 className="h-4 w-4" />}
            accent={STAT_ACCENTS.clients}
            label="Clients"
            value={stats.clients}
          />
          <Stat
            icon={<Boxes className="h-4 w-4" />}
            accent={STAT_ACCENTS.types}
            label="Types"
            value={stats.types}
          />
          <Stat
            icon={<Landmark className="h-4 w-4" />}
            accent={STAT_ACCENTS.value}
            label="Valeur flotte"
            value={fmtEur(stats.fleetValue)}
          />
        </div>

        {/* Répartition par statut */}
        {statuses.length > 0 && (
          <div>
            <h3 className="eyebrow mb-3">
              <Activity className="h-3.5 w-3.5" />
              Repartition par statut
            </h3>
            <div className="flex flex-wrap gap-2">
              {statuses.map(([status, count]) => (
                <span key={status} className={statusPill(status)}>
                  <span className="pill-dot" />
                  {status}
                  <span className="num font-bold">{count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Pipeline du cycle de vie : barre segmentée + étapes */}
        {stats.byLifecycle && lifecycleTotal > 0 && (
          <div>
            <h3 className="eyebrow mb-3">
              <Recycle className="h-3.5 w-3.5" />
              Cycle de vie de la flotte
            </h3>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-inset mb-3">
              {lifecycleEntries.map((s) => (
                <span
                  key={s}
                  className={`${STAGE_BAR[s]} transition-all duration-300`}
                  style={{ width: `${((stats.byLifecycle![s] ?? 0) / lifecycleTotal) * 100}%` }}
                  title={`${LIFECYCLE_META[s].label} : ${stats.byLifecycle![s]}`}
                />
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              {lifecycleEntries.map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5">
                  <LifecycleBadge status={s} />
                  <span className="num text-sm font-bold t-strong">{stats.byLifecycle![s]}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

export function HomePage() {
  const [stats, setStats] = useState<StatsByEnv>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetchStats()
      .then((r) => setStats(r.data))
      .catch((e) => setError((e as Error).message))
      .finally(() => setLoading(false));
  }, []);

  const available = ENVS.filter((e) => stats[e]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">Vue d'ensemble</h1>
          <p className="page-desc">Toute la flotte IoT — production et staging, en temps reel.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/inventaire" className="btn-outline">
            Vue generale
          </Link>
          <Link to="/clients" className="btn-outline">
            Vue client
          </Link>
        </div>
      </div>

      {loading && <div className="t-muted">Chargement des indicateurs...</div>}
      {error && !loading && <div className="text-red-400">{error}</div>}
      {!loading && !error && available.length === 0 && (
        <div className="t-muted">Aucune donnee disponible.</div>
      )}
      {!loading &&
        !error &&
        available.map((e) => <EnvSection key={e} stats={stats[e] as EnvStats} />)}
    </div>
  );
}
